/**
 * DealWonModal — celebration modal that fires when a lead is moved to Closed.
 *
 * Trigger: any code fires `window.dispatchEvent(new CustomEvent('deal:won', {
 *   detail: { leadId, address, city, state, profit? }
 * }))`. This component listens at the shell level and shows the modal.
 *
 * The shareable image is drawn client-side on a canvas — no server round-trip,
 * no LLM cost, works forever. 1200×630 (Twitter/OG card size). Users can:
 *   • Download the PNG
 *   • Copy the PNG to clipboard (where supported)
 *   • Copy the caption text
 *   • Post to Twitter (opens compose URL with pre-filled text)
 *   • Native share sheet (mobile / desktop where supported)
 */
import { useEffect, useRef, useState } from "react";
import { Trophy, Download, Copy, Twitter, Share2, X, DollarSign, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SHARE_HASHTAG = "iSpeedToClose";
const SHARE_SITE_URL = "https://ispeedtoclose.com";

// Build the share URL with attribution params so every click can be traced
// back to the sharing user and credited on conversion.
function buildShareUrl(refUserId) {
  if (!refUserId) return SHARE_SITE_URL;
  const u = new URL(SHARE_SITE_URL);
  u.searchParams.set("ref", refUserId);
  u.searchParams.set("via", "deal_won");
  return u.toString();
}

function useDealWonEvent() {
  const [deal, setDeal] = useState(null);
  useEffect(() => {
    const onEvt = (e) => setDeal(e.detail || {});
    window.addEventListener("deal:won", onEvt);
    return () => window.removeEventListener("deal:won", onEvt);
  }, []);
  return [deal, setDeal];
}

function fmtMoney(n) {
  if (!n) return null;
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return null;
  return `$${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// Draws a premium branded share card onto the given canvas.
function drawCard(canvas, { firstName, address, cityState, profit }) {
  const ctx = canvas.getContext("2d");
  const W = 1200, H = 630;
  canvas.width = W; canvas.height = H;

  // Base gradient — charcoal → deep navy
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#0B132B");
  bg.addColorStop(1, "#1E293B");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Gold accent radial
  const glow = ctx.createRadialGradient(W * 0.85, H * 0.15, 40, W * 0.85, H * 0.15, 700);
  glow.addColorStop(0, "rgba(245, 158, 11, 0.35)");
  glow.addColorStop(1, "rgba(245, 158, 11, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Purple accent radial
  const purple = ctx.createRadialGradient(W * 0.1, H * 0.9, 40, W * 0.1, H * 0.9, 600);
  purple.addColorStop(0, "rgba(139, 92, 246, 0.28)");
  purple.addColorStop(1, "rgba(139, 92, 246, 0)");
  ctx.fillStyle = purple;
  ctx.fillRect(0, 0, W, H);

  // Gold "DEAL CLOSED" chip
  ctx.fillStyle = "#F59E0B";
  ctx.fillRect(80, 90, 200, 40);
  ctx.fillStyle = "#0B132B";
  ctx.font = "bold 18px system-ui, -apple-system, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText("★  DEAL CLOSED", 100, 111);

  // Main headline
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "bold 62px system-ui, -apple-system, sans-serif";
  if (profit) {
    ctx.fillText(`Just closed a ${profit}`, 80, 220);
    ctx.font = "bold 62px system-ui, -apple-system, sans-serif";
    ctx.fillText("wholesale deal.", 80, 295);
  } else {
    ctx.fillText("Just closed another", 80, 220);
    ctx.fillText("wholesale deal.", 80, 295);
  }

  // Address + city
  ctx.fillStyle = "#F59E0B";
  ctx.font = "600 26px system-ui, -apple-system, sans-serif";
  const addressLine = (address || "").slice(0, 40);
  ctx.fillText(addressLine, 80, 370);
  if (cityState) {
    ctx.fillStyle = "#94A3B8";
    ctx.font = "500 22px system-ui, -apple-system, sans-serif";
    ctx.fillText(cityState, 80, 405);
  }

  // Byline
  if (firstName) {
    ctx.fillStyle = "#CBD5E1";
    ctx.font = "500 22px system-ui, -apple-system, sans-serif";
    ctx.fillText(`— ${firstName}`, 80, 480);
  }

  // Powered-by footer
  ctx.fillStyle = "#F59E0B";
  ctx.font = "bold 26px system-ui, -apple-system, sans-serif";
  ctx.fillText("iSpeedToClose", 80, H - 60);
  ctx.fillStyle = "#94A3B8";
  ctx.font = "500 18px system-ui, -apple-system, sans-serif";
  ctx.fillText("The operating system for real estate investors.", 80, H - 32);
}

export default function DealWonModal() {
  const [deal, setDeal] = useDealWonEvent();
  const canvasRef = useRef(null);
  const [profitInput, setProfitInput] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!deal || !canvasRef.current) return;
    drawCard(canvasRef.current, {
      firstName: deal.firstName,
      address:   deal.address,
      cityState: [deal.city, deal.state].filter(Boolean).join(", "),
      profit:    fmtMoney(profitInput),
    });
  }, [deal, profitInput]);

  if (!deal) return null;

  const profit = fmtMoney(profitInput);
  const cityState = [deal.city, deal.state].filter(Boolean).join(", ");
  const shareUrl = buildShareUrl(deal.userId);
  const caption = [
    profit ? `Just closed a ${profit} wholesale deal.` : "Just closed another wholesale deal.",
    cityState ? `📍 ${cityState}` : "",
    `Powered by iSpeedToClose · ${shareUrl}`,
    `#${SHARE_HASHTAG} #realestateinvesting #wholesaling`,
  ].filter(Boolean).join("\n");

  const close = () => { setDeal(null); setProfitInput(""); setCopied(false); };

  const downloadPng = () => {
    if (!canvasRef.current) return;
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `deal-closed-${Date.now()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const copyImage = async () => {
    if (!canvasRef.current || !navigator.clipboard || !window.ClipboardItem) {
      toast.error("Copy image not supported — use Download instead.");
      return;
    }
    try {
      canvasRef.current.toBlob(async (blob) => {
        await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
        toast.success("Image copied to clipboard.");
      });
    } catch {
      toast.error("Couldn't copy the image.");
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      toast.success("Caption copied.");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Couldn't copy the caption."); }
  };

  const tweet = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const nativeShare = async () => {
    if (!navigator.share) { copyCaption(); return; }
    try {
      await navigator.share({ text: caption, url: shareUrl });
    } catch { /* user cancelled */ }
  };

  return (
    <div
      data-testid="deal-won-modal"
      onClick={close}
      className="fixed inset-0 z-[90] bg-slate-900/80 backdrop-blur-sm grid place-items-center p-3 animate-in fade-in duration-200"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        {/* Confetti-ish header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="absolute inset-0"
               style={{ backgroundImage: "linear-gradient(135deg, #F59E0B 0%, #EA580C 60%, #8B5CF6 100%)" }} />
          <div className="relative p-6 text-white text-center">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/20 backdrop-blur px-2.5 py-1 text-[10px] font-black tracking-widest uppercase">
              <Trophy className="w-3 h-3" /> Deal Closed
            </div>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold leading-tight">
              Congrats — you just closed a deal.
            </h2>
            <p className="mt-1 text-white/85 text-[13px]">
              Post it. Own the moment. Compound your reputation.
            </p>
          </div>
          <button type="button" onClick={close}
                  data-testid="deal-won-modal-close"
                  className="absolute top-3 right-3 p-1.5 rounded-full bg-black/20 hover:bg-black/35 text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 md:p-6 space-y-4">
          {/* Deal summary line */}
          <div className="flex items-start gap-2 text-[13px]">
            <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold text-slate-900">{deal.address || "—"}</div>
              {cityState && <div className="text-slate-500">{cityState}</div>}
            </div>
          </div>

          {/* Profit input (optional) */}
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Profit / Assignment Fee (optional)
            </label>
            <input
              type="number"
              inputMode="numeric"
              value={profitInput}
              onChange={(e) => setProfitInput(e.target.value)}
              placeholder="12000"
              data-testid="deal-won-profit-input"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
            />
            <div className="text-[10.5px] text-slate-400 mt-1">
              Adds the amount to the share image + caption. Numbers convert.
            </div>
          </div>

          {/* Canvas preview */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
            <canvas
              ref={canvasRef}
              data-testid="deal-won-canvas"
              className="block w-full h-auto"
              style={{ aspectRatio: "1200 / 630" }}
            />
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <button type="button" onClick={downloadPng}
                    data-testid="deal-won-download-btn"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12.5px] font-semibold px-3 py-2.5 shadow-md">
              <Download className="w-4 h-4" /> Download
            </button>
            <button type="button" onClick={copyImage}
                    data-testid="deal-won-copy-image-btn"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 hover:border-slate-300 bg-white text-slate-900 text-[12.5px] font-semibold px-3 py-2.5">
              <Copy className="w-4 h-4" /> Copy image
            </button>
            <button type="button" onClick={tweet}
                    data-testid="deal-won-tweet-btn"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg text-white text-[12.5px] font-semibold px-3 py-2.5 shadow-md"
                    style={{ backgroundColor: "#1DA1F2" }}>
              <Twitter className="w-4 h-4" /> Tweet
            </button>
            <button type="button" onClick={nativeShare}
                    data-testid="deal-won-share-btn"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg text-white text-[12.5px] font-semibold px-3 py-2.5 shadow-md"
                    style={{ backgroundImage: "linear-gradient(135deg, #10B981, #059669)" }}>
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>

          {/* Caption preview */}
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Caption</div>
              <button type="button" onClick={copyCaption}
                      data-testid="deal-won-copy-caption-btn"
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-slate-900">
                {copied ? <><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
            </div>
            <pre className="text-[12.5px] text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
{caption}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// Convenience helper — any component that closes a deal can call this
// to fire the celebration event with the right shape. Include `userId`
// so the share URL/caption can be tagged with attribution params.
export function celebrateDeal({ leadId, address, city, state, firstName, userId }) {
  window.dispatchEvent(new CustomEvent("deal:won", {
    detail: { leadId, address, city, state, firstName, userId },
  }));
}
